import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { API_BASE_URL } from "../config";
import Loader from "./Loader";
import { Link } from "react-router-dom";
import {
  buildCreditDebitChart,
  buildSalesByMonth,
  buildStockQuantityChart,
  countRecentSales,
  formatCurrency,
  getStockListForDashboard,
  sumCustomerTotals,
  sumExpenseTotals,
  sumSalesTotal,
  sumStockTotals,
} from "../utils/dashboardStats";

function StatCard({ label, value, hint, variant = "primary", to }) {
  const card = (
    <div
      className={`dashboard-stat dashboard-stat--${variant}${to ? " dashboard-stat--clickable" : ""}`}
      style={to ? { cursor: "pointer" } : undefined}
    >
      <span className="dashboard-stat__label">{label}</span>
      <strong className="dashboard-stat__value">{value}</strong>
      {hint ? <span className="dashboard-stat__hint">{hint}</span> : null}
    </div>
  );

  return to ? (
    <Link
      to={to}
      className="dashboard-stat-link"
      style={{ textDecoration: "none" }}
    >
      {card}
    </Link>
  ) : (
    card
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="card app-screen-card dashboard-chart-card">
      <div className="dashboard-chart-card__head">
        <h2 className="dashboard-chart-card__title">{title}</h2>
        {subtitle ? (
          <p className="dashboard-chart-card__subtitle">{subtitle}</p>
        ) : null}
      </div>
      <div className="dashboard-chart-card__body">{children}</div>
    </div>
  );
}

const tooltipStyle = {
  borderRadius: 10,
  border: "1px solid #e2e8f0",
  fontSize: 13,
};

export default function Home() {
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setError("");
      try {
        const [customersRes, salesRes, stocksRes, expensesRes] =
          await Promise.all([
            axios.get(`${API_BASE_URL}/api/customers`),
            axios.get(`${API_BASE_URL}/api/sales`),
            axios.get(`${API_BASE_URL}/api/stocks`),
            axios.get(`${API_BASE_URL}/api/expenses`),
          ]);
        if (!cancelled) {
          setCustomers(customersRes.data);
          setSales(salesRes.data);
          setStocks(stocksRes.data);
          setExpenses(Array.isArray(expensesRes.data) ? expensesRes.data : []);
        }
      } catch (err) {
        console.error("Dashboard load failed", err);
        if (!cancelled) {
          setError("Could not load dashboard data. Check your connection.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const totals = useMemo(() => sumCustomerTotals(customers), [customers]);
  const totalSales = useMemo(() => sumSalesTotal(sales), [sales]);
  const totalExpenses = useMemo(() => sumExpenseTotals(expenses), [expenses]);
  const salesByMonth = useMemo(() => buildSalesByMonth(sales, 6), [sales]);
  const creditDebitData = useMemo(
    () => buildCreditDebitChart(customers),
    [customers],
  );
  const recentCount = useMemo(() => countRecentSales(sales, 30), [sales]);
  const stockTotals = useMemo(() => sumStockTotals(stocks), [stocks]);
  const stockChartData = useMemo(
    () => buildStockQuantityChart(stocks),
    [stocks],
  );
  const stockList = useMemo(() => getStockListForDashboard(stocks), [stocks]);

  if (loading) {
    return <Loader message="Loading dashboard…" fullPage />;
  }

  return (
    <div className="container-fluid app-container dashboard-page">
      {error ? (
        <div className="alert alert-warning dashboard-alert" role="alert">
          {error}
        </div>
      ) : null}

      <div className="dashboard-stats">
        <StatCard
          label="Total sales"
          value={formatCurrency(totalSales)}
          hint={`${sales.length} transactions`}
          variant="primary"
          to="/sales"
        />
        <StatCard
          label="Total credit (CR)"
          value={formatCurrency(totals.credit)}
          hint="Payments received"
          variant="success"
          to="/customers"
        />
        <StatCard
          label="Total debit (DR)"
          value={formatCurrency(totals.debit)}
          hint="Charges / sales due"
          variant="warning"
          to="/customers"
        />
        <StatCard
          label="Net balance"
          value={formatCurrency(totals.balance)}
          hint={`${customers.length} customers`}
          variant="neutral"
        />
        <StatCard
          label="Total expenses"
          value={formatCurrency(totalExpenses)}
          hint={`${expenses.length} records`}
          variant="warning"
          to="/expenses"
        />
        <StatCard
          label="Stock items"
          value={String(stockTotals.items)}
          hint={`${stockTotals.quantity} units in stock`}
          variant="stock"
          to="/stocks"
        />
        <StatCard
          label="Stock value"
          value={formatCurrency(stockTotals.value)}
          hint={
            stockTotals.lowStock > 0
              ? `${stockTotals.lowStock} low stock`
              : "Inventory at price"
          }
          variant="stock"
        />
      </div>

      <div className="row g-3 dashboard-charts">
        <div className="col-12 col-lg-7">
          <ChartCard title="Sales trend" subtitle="Last 6 months">
            {salesByMonth.every((m) => m.sales === 0) ? (
              <p className="dashboard-empty">No sales data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={salesByMonth}
                  margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8edf2" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) =>
                      v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                    }
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(value) => [formatCurrency(value), "Sales"]}
                  />
                  <Bar
                    dataKey="sales"
                    fill="#1a76d1"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        <div className="col-12 col-lg-5">
          <ChartCard title="Credit vs debit" subtitle="All customer accounts">
            {creditDebitData.length === 0 ? (
              <p className="dashboard-empty">No CR/DR data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={creditDebitData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={88}
                    paddingAngle={3}
                  >
                    {creditDebitData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Legend
                    iconType="circle"
                    wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        <div className="col-12">
          <ChartCard title="Stock levels" subtitle="Quantity by item (top 8)">
            {stockChartData.length === 0 ? (
              <p className="dashboard-empty">No stock items yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={stockChartData}
                  layout="vertical"
                  margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8edf2" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={72}
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(value, _name, props) => [
                      `${value} units`,
                      props.payload.fullName,
                    ]}
                  />
                  <Bar
                    dataKey="quantity"
                    fill="#0ea5e9"
                    radius={[0, 6, 6, 0]}
                    maxBarSize={22}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      </div>

      <div className="card app-screen-card dashboard-stock-list">
        <div className="dashboard-stock-list__head">
          <div>
            <h2 className="dashboard-chart-card__title mb-0">Stock overview</h2>
            <p className="dashboard-chart-card__subtitle mb-0">
              Top items by quantity
            </p>
          </div>
          <Link to="/stocks" className="btn btn-sm btn-outline-primary">
            Manage stock
          </Link>
        </div>
        {stockList.length === 0 ? (
          <p className="dashboard-empty mb-0">No stock items yet.</p>
        ) : (
          <ul className="dashboard-stock-items">
            {stockList.map((item) => (
              <li
                key={item.id}
                className={
                  item.isLow
                    ? "dashboard-stock-item is-low"
                    : "dashboard-stock-item"
                }
              >
                <div className="dashboard-stock-item__main">
                  <strong>{item.name}</strong>
                  <span className="dashboard-stock-item__sku">{item.sku}</span>
                </div>
                <div className="dashboard-stock-item__meta">
                  <span className="dashboard-stock-item__qty">
                    {item.quantity} units
                  </span>
                  <span className="dashboard-stock-item__price">
                    {formatCurrency(item.value)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card app-screen-card dashboard-summary">
        <h2 className="dashboard-chart-card__title mb-3">Quick summary</h2>
        <ul className="dashboard-summary__list">
          <li>
            <span>Sales this month</span>
            <strong>{recentCount}</strong>
          </li>
          <li>
            <span>Active customers</span>
            <strong>{customers.length}</strong>
          </li>
          <li>
            <span>Credit share</span>
            <strong>
              {totals.debit + totals.credit > 0
                ? `${Math.round((totals.credit / (totals.debit + totals.credit)) * 100)}%`
                : "—"}
            </strong>
          </li>
          <li>
            <span>Total stock units</span>
            <strong>{stockTotals.quantity}</strong>
          </li>
          <li>
            <span>Total expenses</span>
            <strong>{formatCurrency(totalExpenses)}</strong>
          </li>
          <li>
            <span>Low stock (≤5)</span>
            <strong>{stockTotals.lowStock}</strong>
          </li>
        </ul>
      </div>
    </div>
  );
}
